import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Clock } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { PinnedNotice } from "@/components/PinnedNotice";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TeamworkPublic() {
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/teamwork/public").then(({ data }) => setBoard(data)).catch(() => setBoard({ blocks: [] }));
  }, []);

  useSeo({
    title: "How a crew keeps in rhythm — the shape of our working day | Unity & Utility",
    description: "Handover, huddle, work blocks, break windows, lunch and wind-down: the rhythm a crew works to, with today's in-step score and the kudos they gave each other.",
    path: "/teamwork",
    image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Teamwork", "/teamwork"]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="teamwork-public-page">
        <Crumbs items={[["Home", "/"], ["Teamwork", "/teamwork"]]} />
        <p className="overline text-[#B3241B]">Teamwork rhythm</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <Users size={34} weight="duotone" className="mt-1 shrink-0 text-[#002FA7]" />
          The shape of our working day
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="teamwork-public-intro">
          A crew works better to a rhythm than to a rush: a proper handover, a short huddle, work
          blocks with real break windows between them, lunch taken away from the machine and a
          tidy wind-down. Here is ours, and how in step we were today.
        </p>
        <div className="mt-5">
          <ShareBar
            url={board?.share_token ? `${window.location.origin}/p/${board.share_token}` : `${window.location.origin}/teamwork`}
            text={board?.score != null ? `Our crew rhythm today — in step ${board.score}%` : "The shape of a crew's working day"}
            testid="teamwork-public-share" instagram />
        </div>

        {board?.image_url && (
          <img src={`${BACKEND}${board.image_url}`} alt="Our crew at shift change" loading="lazy"
            className="mt-8 h-56 w-full border border-border object-cover sm:h-80" data-testid="teamwork-public-image" />
        )}

        <div className="mt-8 grid gap-3 sm:grid-cols-3" data-testid="teamwork-public-score">
          <div className="border border-[#002FA7] bg-white p-4">
            <p className="overline text-[#002FA7]">In step today</p>
            <p className="mt-1 text-3xl font-bold text-[#0F172A]">{board?.score != null ? `${board.score}%` : "—"}</p>
            {board?.auto_posted && (
              <span data-testid="teamwork-public-posted" className="overline text-[0.6rem] text-[#0B5C2E]">Posted to Facebook</span>
            )}
          </div>
          <div className="border border-border bg-white p-4">
            <p className="overline text-slate-500">This week vs last</p>
            <p className="mt-1 text-sm text-slate-700">
              {board?.trend?.this_week ?? "—"}% vs {board?.trend?.last_week ?? "—"}% · {board?.trend?.direction || "flat"}
            </p>
          </div>
          <div className="border border-border bg-white p-4">
            <p className="overline text-slate-500">
              <Clock size={12} className="mr-1 inline align-[-2px]" />Right now
            </p>
            <p className="mt-1 text-sm text-slate-700" data-testid="teamwork-public-now">
              {board?.now ? `${board.now.label} (${board.now.start}–${board.now.end})` : "Off shift"}
            </p>
          </div>
        </div>

        {board?.read && (
          <p className="mt-6 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-3 text-sm leading-relaxed text-[#0F172A]"
            data-testid="teamwork-public-read">{board.read}</p>
        )}

        <h2 className="font-display mt-10 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
          {board?.crew || "The crew"}{board?.shift ? ` · ${board.shift}` : ""}
        </h2>
        <div className="mt-3 space-y-2" data-testid="teamwork-public-blocks">
          {(board?.blocks || []).map((b, i) => (
            <div key={i} data-testid={`teamwork-public-block-${i}`}
              className="flex flex-wrap items-baseline gap-x-3 border border-border bg-white px-4 py-3">
              <span className="font-mono text-xs text-slate-500">{b.start} – {b.end}</span>
              <span className="text-sm font-semibold text-[#0F172A]">{b.label}</span>
              <span className="overline text-[0.6rem] text-slate-400">{b.kind}</span>
            </div>
          ))}
        </div>

        {(board?.kudos || []).length > 0 && (
          <section className="mt-10" data-testid="teamwork-public-kudos">
            <p className="overline text-slate-500">Kudos on the floor · +{board.kudos_points} points each</p>
            <ul className="mt-2 space-y-1 text-sm text-slate-700">
              {board.kudos.map((k, i) => (
                <li key={i}>
                  <span className="font-semibold text-[#0F172A]">{k.from_name}</span> thanked {k.to_name}
                  {k.note ? ` · ${k.note}` : ""}
                </li>
              ))}
            </ul>
          </section>
        )}

        <PinnedNotice testid="teamwork-pinned-notice" />

        <Link to="/notices" data-testid="teamwork-public-to-notices"          className="tap-safe mt-8 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
          See the house notices
        </Link>
      </div>
    </PublicShell>
  );
}
